import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Radar, ClipboardList, Rss, Filter } from "lucide-react";
import api from "@/lib/api";

const SEV_STYLE = {
    3: { bg: "bg-[#9E2A2B]", text: "text-white",    label: "crítico"  },
    2: { bg: "bg-[#D4A373]", text: "text-white",    label: "atenção" },
    1: { bg: "bg-[#F5E6C4]", text: "text-[#8A6A15]", label: "leve"    },
    0: { bg: "bg-[#EDF3E8]", text: "text-[#556B2F]", label: "ok"     },
};

function SevDot({ severity }) {
    const s = SEV_STYLE[severity] || SEV_STYLE[0];
    return (
        <span
            className={`inline-block w-3.5 h-3.5 rounded-full ${s.bg}`}
            title={s.label}
        />
    );
}

function HeatCell({ severity, findings }) {
    const s = SEV_STYLE[severity] || SEV_STYLE[0];
    const tip = findings?.length ? findings.join(" · ") : s.label;
    return (
        <div className={`rounded-md px-2 py-1.5 ${s.bg} ${s.text} text-[10px] font-medium`} title={tip}>
            {severity >= 2 ? (findings[0] ? findings[0].slice(0, 22) : s.label) : s.label}
        </div>
    );
}

export default function CockpitView() {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeDomain, setActiveDomain] = useState(null);
    const [modality, setModality] = useState("");
    const [insurance, setInsurance] = useState("");
    const [ageMin, setAgeMin] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/monitoring/cockpit");
                setState(data);
            } finally { setLoading(false); }
        })();
    }, []);

    const filteredRows = useMemo(() => {
        if (!state) return [];
        return state.patients.filter((r) => {
            if (activeDomain && (r.domains[activeDomain]?.severity ?? 0) < 2) return false;
            if (modality && r.consultation_type !== modality) return false;
            if (insurance && r.insurance !== insurance) return false;
            if (ageMin && (r.age ?? 0) < parseInt(ageMin, 10)) return false;
            return true;
        });
    }, [state, activeDomain, modality, insurance, ageMin]);

    if (loading) return <p className="text-muted-clinical">Carregando cockpit…</p>;
    if (!state) return <p className="text-muted-clinical">Sem dados.</p>;

    return (
        <div>
            {/* KPI grid — 5 clinical domains */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="cockpit-kpis">
                {state.domains.map((d) => {
                    const k = state.kpis[d.key];
                    const isActive = activeDomain === d.key;
                    return (
                        <button
                            key={d.key}
                            onClick={() => setActiveDomain(isActive ? null : d.key)}
                            data-testid={`cockpit-kpi-${d.key}`}
                            className={`card-earthy text-left transition-all ${isActive ? "ring-2 ring-brand shadow-lg" : "hover:shadow-md"}`}
                        >
                            <div className="text-[10px] uppercase tracking-widest text-[var(--brand-secondary)] font-semibold mb-2">{d.label}</div>
                            <div className="flex items-baseline gap-3">
                                <div className="font-display text-2xl font-semibold text-[#9E2A2B]">{k.critical}</div>
                                <div className="text-xs text-muted-clinical">crítico{k.critical !== 1 ? "s" : ""}</div>
                            </div>
                            <div className="flex items-center gap-1.5 mt-2 text-[10px] text-muted-clinical">
                                <span className="inline-block w-2 h-2 rounded-full bg-[#D4A373]" /> {k.warning} atenção
                                <span className="inline-block w-2 h-2 rounded-full bg-[#EDF3E8] ml-2" /> {k.stable} ok
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Filters */}
            <div className="card-earthy mb-4 flex flex-wrap items-center gap-3" data-testid="cockpit-filters">
                <div className="flex items-center gap-2 text-xs text-muted-clinical">
                    <Filter size={12} /> Filtrar
                </div>
                <select value={modality} onChange={(e) => setModality(e.target.value)} className="input-strong text-xs" data-testid="filter-modality">
                    <option value="">Todas modalidades</option>
                    <option value="ambulatorial">Ambulatorial</option>
                    <option value="domiciliar">Domiciliar</option>
                    <option value="telemedicina_canabis">Canabis</option>
                    <option value="retorno">Retorno</option>
                </select>
                <select value={insurance} onChange={(e) => setInsurance(e.target.value)} className="input-strong text-xs" data-testid="filter-insurance">
                    <option value="">Todos convênios</option>
                    <option value="Particular">Particular</option>
                    <option value="Convênio">Convênio</option>
                    <option value="SUS">SUS</option>
                </select>
                <input
                    type="number" min={60} max={110}
                    value={ageMin} onChange={(e) => setAgeMin(e.target.value)}
                    placeholder="Idade ≥"
                    className="input-strong text-xs w-24"
                    data-testid="filter-age"
                />
                {(activeDomain || modality || insurance || ageMin) && (
                    <button
                        onClick={() => { setActiveDomain(null); setModality(""); setInsurance(""); setAgeMin(""); }}
                        className="btn-ghost text-xs"
                        data-testid="filter-clear"
                    >
                        Limpar
                    </button>
                )}
                <span className="ml-auto text-xs text-muted-clinical">
                    {filteredRows.length} paciente{filteredRows.length !== 1 ? "s" : ""}
                </span>
            </div>

            {/* Heatmap table */}
            <div className="card-earthy overflow-x-auto" data-testid="cockpit-heatmap">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-widest text-[var(--brand-secondary)]">
                            <th className="text-left py-2 pr-3">Paciente</th>
                            {state.domains.map((d) => (
                                <th key={d.key} className="text-center py-2 px-1 min-w-[110px]">{d.label.split(" ")[0]}</th>
                            ))}
                            <th className="text-right py-2 pl-3">Score</th>
                            <th className="text-center py-2 pl-3">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(30,63,51,0.08)]">
                        {filteredRows.map((r) => (
                            <tr key={r.patient_id} className="hover:bg-alt/40" data-testid={`cockpit-row-${r.patient_id}`}>
                                <td className="py-2.5 pr-3">
                                    <Link to={`/monitoring/patient/${r.patient_id}`} className="text-brand font-medium hover:underline">
                                        {r.patient_name}
                                    </Link>
                                    <div className="text-[10px] text-muted-clinical">
                                        {r.age ? `${r.age}a` : "—"}{r.insurance ? ` · ${r.insurance}` : ""}
                                    </div>
                                </td>
                                {state.domains.map((d) => {
                                    const b = r.domains[d.key];
                                    return (
                                        <td key={d.key} className="py-2.5 px-1 text-center">
                                            <HeatCell severity={b?.severity || 0} findings={b?.findings || []} />
                                        </td>
                                    );
                                })}
                                <td className="py-2.5 pl-3 text-right font-display text-lg font-semibold text-[var(--brand-accent)]">{r.risk_score}</td>
                                <td className="py-2.5 pl-3">
                                    <div className="flex gap-1 justify-center">
                                        <Link
                                            to={`/patients/${r.patient_id}`}
                                            className="text-[10px] px-2 py-1 rounded-md bg-brand text-white hover:bg-[#173026] transition-colors"
                                            data-testid={`action-open-${r.patient_id}`}
                                        >
                                            Abrir
                                        </Link>
                                        <Link
                                            to={`/monitoring/patient/${r.patient_id}`}
                                            className="text-[10px] px-2 py-1 rounded-md bg-[#2E7D6B] text-white hover:bg-[#1F5A4C] transition-colors"
                                            data-testid={`action-plan-${r.patient_id}`}
                                        >
                                            Plano IA
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredRows.length === 0 && (
                            <tr>
                                <td colSpan={state.domains.length + 3} className="py-8 text-center text-sm text-muted-clinical">
                                    Nenhum paciente atende aos filtros selecionados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 text-[11px] text-muted-clinical">
                <div className="flex items-center gap-1.5"><SevDot severity={3} /> crítico</div>
                <div className="flex items-center gap-1.5"><SevDot severity={2} /> atenção</div>
                <div className="flex items-center gap-1.5"><SevDot severity={1} /> leve</div>
                <div className="flex items-center gap-1.5"><SevDot severity={0} /> ok</div>
            </div>
        </div>
    );
}
